import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { IconButton, Stack, Tooltip } from "@mui/material";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import type { MembersQuery } from "@/libs/generated/operations";
import { formatLongDate } from "@/utils/date";

type MemberRecord = MembersQuery["members"]["records"][0];

type ActionHandlers = {
  onEdit?: (member: MemberRecord) => void;
  onDelete?: (member: MemberRecord) => void;
};

// ─── Shared Cell Components ───────────────────────────────────────────────────

function ActionsCell({ row }: { row: { original: MemberRecord } }) {
  const { t } = useTranslation();
  const handlers = (row.original as MemberRecord & { _actions?: ActionHandlers })._actions;

  const getButtonSx = (color: string) => ({
    border: 1,
    borderColor: "divider",
    boxShadow: 1,
    "&:hover": { boxShadow: 2 },
    color: `${color}.main`,
  });

  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", justifyContent: "center" }}>
      {handlers?.onEdit && (
        <Tooltip title={t("common.actions.edit")}>
          <IconButton
            onClick={() => handlers.onEdit?.(row.original)}
            size="small"
            sx={getButtonSx("primary")}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {handlers?.onDelete && (
        <Tooltip title={t("common.actions.delete")}>
          <IconButton
            onClick={() => handlers.onDelete?.(row.original)}
            size="small"
            sx={getButtonSx("error")}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

// ─── Table Columns ───────────────────────────────────────────────────────────────

const BASE_MEMBERS_TABLE_COLUMNS: ColumnDef<MemberRecord>[] = [
  {
    accessorKey: "assigneeFirstName",
    meta: { label: "Name" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    cell: ({ row }) => (
      <div>
        <div style={{ fontWeight: 600, fontSize: "0.875rem" }}>
          {row.original.assigneeFirstName} {row.original.assigneeLastName}
        </div>
        <div style={{ fontSize: "0.75rem", color: "#666" }}>{row.original.assigneeEmail}</div>
      </div>
    ),
    enableSorting: true,
  },
  {
    accessorKey: "assigneeFunction",
    meta: { label: "Fonction" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Fonction" />,
    cell: ({ row }) => (
      <div>
        <div style={{ fontWeight: 500, fontSize: "0.875rem" }}>
          {row.original.assigneeFunction ?? "-"}
        </div>
        <div style={{ fontSize: "0.75rem", color: "#666" }}>
          {row.original.assigneeService ?? "-"}
        </div>
      </div>
    ),
    enableSorting: true,
  },
  {
    accessorKey: "assigneePhone",
    meta: { label: "Numéro" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Numéro" />,
    cell: ({ row }) => <div style={{ fontSize: "0.875rem" }}>{row.original.assigneePhone}</div>,
    enableSorting: true,
  },
  {
    accessorKey: "createdAt",
    meta: { label: "Créé à" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Créé à" />,
    cell: ({ row }) => (
      <div style={{ fontSize: "0.875rem" }}>{formatLongDate(row.original.createdAt)}</div>
    ),
    enableSorting: true,
  },
  {
    id: "actions",
    meta: { label: "Action" },
    // header: "Action",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
    cell: ({ row }) => <ActionsCell row={row} />,
    enableSorting: false,
    enableHiding: false,
  },
];

export const MEMBERS_TABLE_COLUMNS = BASE_MEMBERS_TABLE_COLUMNS;
